/**
 * Service Command Repository
 *
 * Handles all write operations for services (create, update, delete).
 * This repository is responsible for modifying service data in the database.
 */

import { BaseRepository, RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Service } from '@/shared/types';
import { mapDatabaseServiceToService, mapServiceToDatabaseService } from '@/shared/services/dataMapper';

/**
 * Database Service entity
 */
interface DatabaseService {
  id: string;
  user_id: string;
  name: string;
  rate: number;
  description?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Service Command Repository
 * Provides write operations for the services table
 */
export class ServiceCommandRepository extends BaseRepository<Service> {
  protected tableName = 'services' as const;

  /**
   * Create a new service
   */
  async create(userId: string, serviceData: Omit<Service, 'id' | 'userId'>): Promise<RepositoryResult<Service>> {
    try {
      const db = getDbClient();

      const dbData = {
        user_id: userId,
        ...mapServiceToDatabaseService(serviceData),
      };

      const { data, error } = await db
        .from(this.tableName)
        .insert(dbData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const service = mapDatabaseServiceToService(data as DatabaseService);

      return {
        success: true,
        data: service,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'creating service'),
      };
    }
  }

  /**
   * Update an existing service
   */
  async update(id: string, serviceData: Partial<Service>): Promise<RepositoryResult<Service>> {
    try {
      const db = getDbClient();

      const dbData = mapServiceToDatabaseService(serviceData);
      (dbData as any).updated_at = new Date().toISOString();

      const { data, error } = await db
        .from(this.tableName)
        .update(dbData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Service not found',
        };
      }

      const service = mapDatabaseServiceToService(data as DatabaseService);

      return {
        success: true,
        data: service,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'updating service'),
      };
    }
  }

  /**
   * Delete a service
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'deleting service'),
      };
    }
  }

  /**
   * Batch create services
   */
  async createBatch(
    userId: string,
    servicesData: Omit<Service, 'id' | 'userId'>[]
  ): Promise<RepositoryResult<Service[]>> {
    try {
      const db = getDbClient();

      const dbDataArray = servicesData.map((serviceData) => ({
        user_id: userId,
        ...mapServiceToDatabaseService(serviceData),
      }));

      const { data, error } = await db
        .from(this.tableName)
        .insert(dbDataArray)
        .select();

      if (error) {
        throw error;
      }

      const services = (data || []).map((dbService) =>
        mapDatabaseServiceToService(dbService as DatabaseService)
      );

      return {
        success: true,
        data: services,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'creating services batch'),
      };
    }
  }

  /**
   * Bulk delete services
   */
  async bulkDelete(ids: string[]): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .delete()
        .in('id', ids);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'bulk deleting services'),
      };
    }
  }

  /**
   * Update service rate
   */
  async updateRate(id: string, rate: number): Promise<RepositoryResult<Service>> {
    return this.update(id, { rate });
  }

  /**
   * Batch update rates
   */
  async batchUpdateRates(updates: Array<{ id: string; rate: number }>): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      // Process each update
      for (const { id, rate } of updates) {
        const { error } = await db
          .from(this.tableName)
          .update({ rate, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) {
          throw error;
        }
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'batch updating service rates'),
      };
    }
  }

  /**
   * Duplicate a service
   */
  async duplicateService(serviceId: string): Promise<RepositoryResult<Service>> {
    try {
      // Import dynamically to avoid circular dependency
      const { serviceQueryRepository } = await import('./ServiceQueryRepository');

      // Get the original service
      const result = await serviceQueryRepository.findById(serviceId);

      if (!result.success || !result.data) {
        return {
          success: false,
          error: 'Service not found',
        };
      }

      const original = result.data;

      // Create duplicate with modified name
      const duplicateData: Omit<Service, 'id' | 'userId'> = {
        name: `${original.name} (Copy)`,
        rate: original.rate,
        description: original.description,
      };

      return this.create(original.userId, duplicateData);
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'duplicating service'),
      };
    }
  }
}

// Export singleton instance
export const serviceCommandRepository = new ServiceCommandRepository();
